import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    inject,
    signal,
    ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCheckbox, MatCheckboxModule } from '@angular/material/checkbox';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormCellRendererBaseComponent } from '../../../../../../shared/components/grid/cell-renderers/form-cell-renderer-base/form-cell-renderer-base.component';
import { EmissionDesignatorCellRendererParams } from './models/emission-designator-cell-renderer-params';
import { EmissionDesignator } from '../../models/emission-designator';

@Component({
    selector: 'app-emission-designator-cell-renderer',
    imports: [
        MatCheckboxModule,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './emission-designator-cell-renderer.component.html',
})
export class EmissionDesignatorCellRendererComponent extends FormCellRendererBaseComponent<EmissionDesignatorCellRendererParams> {
    @ViewChild('inputElement') inputElement!: MatCheckbox;

    private readonly destroyRef = inject(DestroyRef);

    // A signal so the cell re-renders when the emission designators change outside of the grid
    // (e.g. after an emission designator operation), since the grid's own containers do not trigger change detection.
    emissionDesignators = signal<EmissionDesignator[]>([]);
    isReadOnly = false;

    agInit(params: EmissionDesignatorCellRendererParams): void {
        this.params = params;
        this.isReadOnly = params.isReadOnly;
        this.formControl = params.context.formArray
            .at(params.node.rowIndex)
            .get(params.column?.getColId()) as FormControl;

        if (params.getEmissionDesignatorsFunction) {
            params
                .getEmissionDesignatorsFunction(params)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((options: EmissionDesignator[]) => {
                    this.emissionDesignators.set(options);
                });
        }
    }

    onFocus(): void {
        if (this.shouldFocus()) {
            this.inputElement.focus();
            this.isFocusedState.set(true);
        }
    }

    onClick(): void {
        if (this.isReadOnly) {
            return;
        }

        this.params.openEmissionDesignatorOperation(this.params);
    }

    getTitle(emissionDesignator: EmissionDesignator): string {
        const { code, actionCode, manualEmissionCode } = emissionDesignator;
        const formattedCode = actionCode ? `${code} (${actionCode})` : code!;

        return manualEmissionCode ? `${formattedCode}**` : formattedCode;
    }
}
